// Capture Amazon OAuth callback params from the URL into sessionStorage BEFORE the
// router strips them during redirects. Ported verbatim from the old router guard
// (src/router/index.js beforeEach top block). Destination pages read these by name.
import { captureAdsClaimContext, ADS_CLAIM_CONTEXT_KEY, ADS_CLAIM_KEY } from './adsClaimContext';
import { captureBingClaimContext, BING_CLAIM_CONTEXT_KEY, BING_CLAIM_KEY } from './bingClaimContext';
import {
  captureEbayClaimContext,
  EBAY_CLAIM_CONTEXT_KEY,
  EBAY_CLAIM_KEY,
} from './ebayClaimContext';
import {
  captureGoogleClaimContext,
  GOOGLE_CLAIM_CONTEXT_KEY,
  GOOGLE_CLAIM_KEY,
} from './googleClaimContext';
import {
  captureMetaClaimContext,
  META_CLAIM_CONTEXT_KEY,
  META_CLAIM_KEY,
} from './metaClaimContext';
import {
  capturePinterestClaimContext,
  PINTEREST_CLAIM_CONTEXT_KEY,
  PINTEREST_CLAIM_KEY,
} from './pinterestClaimContext';
import { captureSpClaimContext, SP_CLAIM_CONTEXT_KEY, SP_CLAIM_KEY } from './spClaimContext';
import {
  captureTikTokClaimContext,
  TIKTOK_CLAIM_CONTEXT_KEY,
  TIKTOK_CLAIM_KEY,
} from './tiktokClaimContext';

// Shopify claim token from App Home's handoff URL: /integrations/shopify/claim#shopifyClaim=<t>.
// It rides the fragment (never the query), so no server, access log or Referer ever sees it.
export const SHOPIFY_CLAIM_HASH_PARAM = 'shopifyClaim';
export const SHOPIFY_CLAIM_KEY = 'shopify_claim';
export const SHOPIFY_CLAIM_ATTEMPT_KEY = 'shopify_claim_attempt';

// The shopifyClaim value of a '#a=b&c=d' fragment, or null.
export const shopifyClaimFromHash = (hash) =>
  new URLSearchParams(String(hash || '').replace(/^#/, '')).get(SHOPIFY_CLAIM_HASH_PARAM) || null;

// Router guard (registered first in router/index.js): moves a shopifyClaim fragment into the
// rescue copy and redirects to the same location without the hash. The router snapshots the
// landing URL before capture() runs, so without this its first navigation would write the
// fragment back into the address bar, and a login redirect would carry it in ?to=.
function stripShopifyClaimHash(to) {
  const token = shopifyClaimFromHash(to.hash);
  if (!token) return undefined;
  sessionStorage.setItem(SHOPIFY_CLAIM_KEY, token);
  return { path: to.path, query: to.query, hash: '', replace: true };
}

function capture() {
  const p = new URLSearchParams(window.location.search);

  // Amazon Ads OAuth
  const adsCode = p.get('code');
  const adsScope = p.get('scope');
  if (adsCode && adsScope) {
    sessionStorage.setItem('amazon_ads_code', adsCode);
    sessionStorage.setItem('amazon_ads_scope', adsScope);
  }
  const adsError = p.get('error');
  if (adsError) {
    sessionStorage.setItem('amazon_ads_error', adsError);
    const adsErrDesc = p.get('error_description');
    if (adsErrDesc) sessionStorage.setItem('amazon_ads_error_description', adsErrDesc);
  }

  const oauthSessionId = p.get('sessionId');
  if (oauthSessionId) captureAdsClaimContext(oauthSessionId, p.get('businessProfileId'));

  // SP claim token from the claim-hardened callback redirect. The landing page reads the
  // URL first, falls back to this copy, and removes it once the claim settles.
  const spSession = p.get('sp_session');
  if (spSession) captureSpClaimContext(spSession, p.get('businessProfileId'));

  // eBay claim token from the eBay callback redirect — same read-then-drop contract as SP.
  const ebayClaim = p.get('ebayClaim');
  if (ebayClaim) captureEbayClaimContext(ebayClaim, p.get('businessProfileId'));

  // Google claim token from the Google callback redirect — same read-then-drop contract as eBay.
  const googleClaim = p.get('googleClaim');
  if (googleClaim) captureGoogleClaimContext(googleClaim, p.get('businessProfileId'));

  // TikTok claim token from the TikTok callback redirect — same read-then-drop contract as Google.
  const tiktokClaim = p.get('tiktokClaim');
  if (tiktokClaim) captureTikTokClaimContext(tiktokClaim, p.get('businessProfileId'));

  // Pinterest claim token from the Pinterest callback redirect — same read-then-drop contract.
  const pinterestClaim = p.get('pinterestClaim');
  if (pinterestClaim) capturePinterestClaimContext(pinterestClaim, p.get('businessProfileId'));

  // Meta claim token from the Meta callback redirect — same read-then-drop contract as Google.
  const metaClaim = p.get('metaClaim');
  if (metaClaim) captureMetaClaimContext(metaClaim, p.get('businessProfileId'));

  // Bing claim token from the Bing callback redirect — same read-then-drop contract as Google.
  const bingClaim = p.get('bingClaim');
  if (bingClaim) captureBingClaimContext(bingClaim, p.get('businessProfileId'));

  // Shopify claim token from the fragment. Scrubbed from the address bar at once (path and
  // query kept) so it does not linger in history; stripShopifyClaimHash covers the router.
  const shopifyClaim = shopifyClaimFromHash(window.location.hash);
  if (shopifyClaim) {
    sessionStorage.setItem(SHOPIFY_CLAIM_KEY, shopifyClaim);
    const { pathname, search } = window.location;
    window.history?.replaceState?.(window.history.state, '', `${pathname}${search}`);
  }

  // Amazon Seller Partner OAuth
  const spCode = p.get('spapi_oauth_code');
  const spSellerId = p.get('selling_partner_id');
  const spState = p.get('state');
  if (spCode && spSellerId) {
    sessionStorage.setItem('amazon_sp_code', spCode);
    sessionStorage.setItem('amazon_sp_seller_id', spSellerId);
    if (spState) sessionStorage.setItem('amazon_sp_state', spState);
  }
  const spError = p.get('error');
  if (spError) {
    sessionStorage.setItem('amazon_sp_error', spError);
    const spErrDesc = p.get('error_description');
    if (spErrDesc) sessionStorage.setItem('amazon_sp_error_description', spErrDesc);
  }
}

// Every sessionStorage key capture() may write — the single source of truth so
// clearCaptured() can drop the whole set on auth transitions. sessionStorage survives a
// logout/login in the same tab, so a leftover oauth_session_id from a previous attempt would
// otherwise be polled under the new session's business profile and 403 forever.
// The connectAccounts.* keys are /connect-accounts' own OAuth round-trip state (the Ads profile
// selection, the chosen onboarding path, the onboarding-session marker, analytics once-flags) —
// just as user-specific, so a logout / user switch drops them too.
// shopify_claim is the exception to "stale on a new session": it comes from Shopify admin, not
// from a flow the previous WisePPC session started, and the merchant often lands signed out.
// Only an explicit logout drops it (keepHandoff below); the claim page still names the shop and
// asks an admin/owner to confirm, and the token expires after 30 minutes.
// A submitted attempt keeps its destination too; restoration requires the same user and token.
// Amazon Ads, eBay, Google, TikTok, Pinterest, Meta, Bing and SP may also return after the WisePPC session expires. Preserve its fixed destination
// through sign-in; the backend still requires the user who started the attempt.
const HANDOFF_KEYS = [
  ADS_CLAIM_KEY,
  ADS_CLAIM_CONTEXT_KEY,
  SP_CLAIM_KEY,
  SP_CLAIM_CONTEXT_KEY,
  SHOPIFY_CLAIM_KEY,
  SHOPIFY_CLAIM_ATTEMPT_KEY,
  EBAY_CLAIM_KEY,
  EBAY_CLAIM_CONTEXT_KEY,
  GOOGLE_CLAIM_KEY,
  GOOGLE_CLAIM_CONTEXT_KEY,
  TIKTOK_CLAIM_KEY,
  TIKTOK_CLAIM_CONTEXT_KEY,
  PINTEREST_CLAIM_KEY,
  PINTEREST_CLAIM_CONTEXT_KEY,
  META_CLAIM_KEY,
  META_CLAIM_CONTEXT_KEY,
  BING_CLAIM_KEY,
  BING_CLAIM_CONTEXT_KEY,
];
const CAPTURED_KEYS = [
  'amazon_ads_code',
  'amazon_ads_scope',
  'amazon_ads_error',
  'amazon_ads_error_description',
  ADS_CLAIM_KEY,
  ADS_CLAIM_CONTEXT_KEY,
  SP_CLAIM_KEY,
  SP_CLAIM_CONTEXT_KEY,
  EBAY_CLAIM_KEY,
  EBAY_CLAIM_CONTEXT_KEY,
  GOOGLE_CLAIM_KEY,
  GOOGLE_CLAIM_CONTEXT_KEY,
  TIKTOK_CLAIM_KEY,
  TIKTOK_CLAIM_CONTEXT_KEY,
  PINTEREST_CLAIM_KEY,
  PINTEREST_CLAIM_CONTEXT_KEY,
  META_CLAIM_KEY,
  META_CLAIM_CONTEXT_KEY,
  BING_CLAIM_KEY,
  BING_CLAIM_CONTEXT_KEY,
  SHOPIFY_CLAIM_KEY,
  SHOPIFY_CLAIM_ATTEMPT_KEY,
  'amazon_sp_code',
  'amazon_sp_seller_id',
  'amazon_sp_state',
  'amazon_sp_error',
  'amazon_sp_error_description',
  'connectAccounts.adsProfilesState',
  'connectAccounts.onboardingPath',
  'connectAccounts.onboardingSession',
  'connectAccounts.onboardingStartedTracked',
  'connectAccounts.oauthReturnTracked',
];

// keepHandoff: a sign-in or a rejected session restore — keep HANDOFF_KEYS so the merchant can
// sign in and still finish the Shopify claim. A logout clears everything.
function clearCaptured({ keepHandoff = false } = {}) {
  for (const key of CAPTURED_KEYS) {
    if (keepHandoff && HANDOFF_KEYS.includes(key)) continue;
    sessionStorage.removeItem(key);
  }
}

export const oauthQueryRescue = { captureFromUrl: capture, clearCaptured, stripShopifyClaimHash };
